import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Zap, Lock, CreditCard, ArrowRight, Clock } from 'lucide-react';
import { Countdown } from './Countdown';
type PlanId = 'megapack' | 'completo';
const plans: {
  id: PlanId;
  emoji: string;
  name: string;
  desc: string;
  priceOld: string;
  price: string;
  priceNum: string;
  badge?: string;
  saving?: string;
  ctaLabel: string;
  noteUnder: string;
  url: string;
}[] = [{
  id: 'megapack',
  emoji: '📘',
  name: 'MEGAPACK',
  desc: '6 tomos + 6 bonos',
  priceOld: '$34.990',
  price: '$19.990',
  priceNum: '19990',
  ctaLabel: 'SÍ, QUIERO EL MEGAPACK',
  noteUnder: 'La base completa para resolver tu año de tenis',
  url: 'https://pagos.recursosfitness.shop/cart/44139580194913:1'
}, {
  id: 'completo',
  emoji: '🏆',
  name: 'PACK COMPLETO',
  desc: '6 tomos + 6 bonos + Tenis Pro Avanzado™',
  priceOld: '$43.990',
  price: '$27.990',
  priceNum: '27990',
  badge: 'MÁS ELEGIDO',
  saving: 'AHORRÁS $16.000',
  ctaLabel: 'SÍ, QUIERO PACK COMPLETO',
  noteUnder: 'La opción más elegida por entrenadores que enseñan con sistema total',
  url: 'https://pagos.recursosfitness.shop/cart/44139832246369:1'
}];
export function PricingSection({
  id = 'pricing'
}: {
  id?: string;
}) {
  const [selected, setSelected] = useState<PlanId>('completo');
  const current = plans.find(p => p.id === selected)!;
  return <section id={id} className="py-8 md:py-16 bg-navy-900 relative overflow-hidden" data-id="element-201">
      <div className="absolute inset-0 bg-court-pattern opacity-20" data-id="element-202"></div>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[300px] bg-optic/10 blur-[120px] rounded-full pointer-events-none" data-id="element-203"></div>

      <div className="max-w-xl mx-auto px-4 sm:px-6 relative z-10" data-id="element-204">
        {/* Urgency banner */}
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="relative mb-5 md:mb-7" data-id="element-205">
          <div className="absolute -inset-1 bg-gradient-to-r from-terracotta via-red-500 to-terracotta blur-lg opacity-60 rounded-2xl" data-id="element-206"></div>
          <div className="relative bg-gradient-to-br from-terracotta to-red-700 border-2 border-white/20 rounded-2xl p-4 md:p-5 shadow-2xl" data-id="element-207">
            <div className="flex items-center justify-center gap-2 mb-3 md:mb-4" data-id="element-208">
              <motion.span animate={{
              scale: [1, 1.2, 1]
            }} transition={{
              duration: 1.2,
              repeat: Infinity
            }} className="w-2 h-2 rounded-full bg-optic" data-id="element-209"></motion.span>
              <Clock className="w-4 h-4 md:w-5 md:h-5 text-white" data-id="element-210" />
              <span className="font-display text-white text-sm md:text-base uppercase tracking-wider" data-id="element-211">
                Precio de lanzamiento termina en:
              </span>
            </div>
            <div className="flex justify-center" data-id="element-212">
              <Countdown data-id="element-213" />
            </div>
            <p className="text-center text-white/90 text-[11px] md:text-xs font-semibold mt-3 md:mt-4 uppercase tracking-wider" data-id="element-214">
              ⚠️ Después sube $8.000 y no vuelve a bajar
            </p>
          </div>
        </motion.div>

        <div className="text-center mb-4 md:mb-6" data-id="element-215">
          <h2 className="text-xl md:text-3xl font-display text-white leading-tight" data-id="element-216">
            Elegí tu Tenis Resuelto:
          </h2>
        </div>

        {/* Stacked offers */}
        <div className="space-y-2.5 md:space-y-3 mb-4 md:mb-5" data-id="element-217">
          {plans.map(plan => {
          const isSelected = selected === plan.id;
          return <button key={plan.id} type="button" onClick={() => setSelected(plan.id)} aria-pressed={isSelected} className={`w-full text-left rounded-2xl px-3.5 md:px-5 py-3 md:py-4 transition-all relative ${isSelected ? 'bg-navy-900 border-2 border-optic shadow-glow' : 'bg-navy-800/80 border-2 border-navy-700 hover:border-optic/40'}`} data-id="element-218">
                <div className="flex items-center gap-3 md:gap-4" data-id="element-219">
                  {/* Radio */}
                  <div className={`flex-shrink-0 w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? 'border-optic' : 'border-gray-500'}`} data-id="element-220">
                    {isSelected && <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-optic" data-id="element-221" />}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0" data-id="element-222">
                    <div className="flex items-center gap-1.5 md:gap-2 mb-0.5 md:mb-1 flex-wrap" data-id="element-223">
                      <span className="text-base md:text-lg" data-id="element-224">{plan.emoji}</span>
                      <h3 className="font-display text-sm md:text-lg text-white tracking-wide" data-id="element-225">
                        {plan.name}
                      </h3>
                    </div>
                    {(plan.badge || plan.saving) && isSelected && <div className="flex items-center gap-1.5 mb-1 flex-wrap" data-id="element-226">
                        {plan.badge && <span className="inline-flex items-center gap-1 bg-optic text-navy-900 text-[9px] md:text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full" data-id="element-227">
                            ⭐ {plan.badge}
                          </span>}
                        {plan.saving && <span className="inline-block bg-terracotta text-white text-[9px] md:text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full" data-id="element-228">
                            {plan.saving}
                          </span>}
                      </div>}
                    <p className="text-[11px] md:text-xs text-gray-400 leading-tight" data-id="element-229">
                      {plan.desc}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="flex-shrink-0 text-right" data-id="element-230">
                    <span className="block text-[10px] md:text-xs text-gray-500 line-through font-bold leading-none mb-0.5" data-id="element-231">
                      {plan.priceOld}
                    </span>
                    <span className={`font-display text-lg md:text-2xl leading-none ${isSelected ? 'text-optic' : 'text-white'}`} data-id="element-232">
                      {plan.price}
                    </span>
                  </div>
                </div>
              </button>;
        })}
        </div>

        {/* CTA */}
        <motion.a key={current.id} href={current.url} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} className="group w-full flex items-center justify-center gap-2 bg-optic text-navy-900 py-3.5 md:py-4 rounded-xl font-display text-sm md:text-lg uppercase tracking-wide shadow-glow hover:bg-white hover:shadow-glow-lg transition-all" data-id="element-233">
          {current.ctaLabel}
          <ArrowRight className="w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform" data-id="element-234" />
        </motion.a>

        <p className="text-center text-gray-400 text-[11px] md:text-xs mt-2 md:mt-3 italic px-2" data-id="element-235">
          {current.noteUnder}
        </p>

        {/* Trust strip */}
        <div className="mt-3 md:mt-5 bg-navy-800/60 border border-navy-700 rounded-xl px-3 py-2.5 md:py-3" data-id="element-236">
          <div className="flex flex-wrap justify-center items-center gap-x-3 gap-y-1 text-[10px] md:text-xs text-gray-300 font-medium" data-id="element-237">
            <div className="flex items-center gap-1" data-id="element-238">
              <Lock className="w-3.5 h-3.5 text-green-400" data-id="element-239" /> Pago seguro
            </div>
            <div className="flex items-center gap-1" data-id="element-240">
              <Zap className="w-3.5 h-3.5 text-optic" data-id="element-241" /> Acceso en 5 min
            </div>
            <div className="flex items-center gap-1" data-id="element-242">
              <ShieldCheck className="w-3.5 h-3.5 text-green-400" data-id="element-243" /> Garantía 7
              días
            </div>
            <div className="flex items-center gap-1" data-id="element-244">
              <CreditCard className="w-3.5 h-3.5 text-blue-400" data-id="element-245" /> Mercado Pago
            </div>
          </div>
        </div>
      </div>
    </section>;
}